'use client';
import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Description, tosVersions } from '@/app/auth/signup/termAndConditions';
import { appAction, appState } from '@/store/store';
import { CURRENT_TOS_VERSION } from '@/utils/config';

/**
 * Blocking re-prompt modal for users who haven't accepted the current
 * ToS version (either never accepted at all, or accepted an older
 * version before a mass-update).
 *
 * Renders the Description sections for `CURRENT_TOS_VERSION` so users
 * see exactly what they're agreeing to. Cannot be dismissed without
 * either clicking "I Agree" (which calls POST /api/user/accept-tos)
 * or signing out.
 */
const TosModal = () => {
  const userObj = appState((state) => state.AuthModel.userObj);
  const acceptTos = appAction((action) => action.AuthModel.acceptTos);
  const [submitting, setSubmitting] = useState(false);

  // Show when we've loaded a user AND either (a) they've never accepted
  // any version, or (b) the version they accepted is behind the current
  // one. Admins are not gated -- admin accounts are provisioned
  // out-of-band and their `tosAcceptedAt` may be null without the user
  // ever having seen a signup form.
  const shouldShow = !!(
    userObj &&
    (userObj.tosAcceptedAt === null || userObj.tosAcceptedVersion !== CURRENT_TOS_VERSION) &&
    (userObj.type === 'advisor' || userObj.type === 'provider')
  );

  // Render the current version's content. Fall back to the historical
  // `Description` export so a misconfigured CURRENT_TOS_VERSION never
  // surfaces an empty modal.
  const sections = tosVersions[CURRENT_TOS_VERSION] ?? Description;

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await acceptTos();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={shouldShow}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
      scrollable
      // No closeButton, no onHide handler — there's no way out except the
      // "I Agree" button or the global Sign Out in the header dropdown.
    >
      <Modal.Header>
        <Modal.Title>Updated Terms of Service</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <p>
          Our Terms of Service have been updated. Please review and accept the terms below to
          continue using Rossby Office.
        </p>
        {sections.map((section) => (
          <div key={section.id} style={{ marginBottom: '16px' }}>
            <h6 style={{ fontWeight: 600 }}>{section.id}</h6>
            <div>{section.desc}</div>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <button className="primaryButton" onClick={handleAccept} disabled={submitting}>
          {submitting ? 'Recording…' : 'I Agree'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default TosModal;
